import { SegmentedControl, Select } from '@mantine/core';
import { type ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeInput } from './DateTimeInput.js';
import classes from './DateTimeLtUtcInput.module.css';
import {
  fromZonedInput,
  ianaZoneOptions,
  offsetChoices,
  offsetLabel,
  parseOffset,
  zonedInput,
  zonedStamp,
} from './lt-utc.js';

export type TimeEntryMode = 'lt' | 'utc';

export interface DateTimeLtUtcInputProps {
  /** Alanın adı; etikete giriş biçimi eklenir: "Let Go / Cast Off (LT)" ya da "(UTC)". */
  label: string;
  /** UTC ISO-8601 an; boşsa `null`. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Raporun saat farkı (dakika): LT = UTC + fark. */
  tzOffsetMin: number;
  required?: boolean;
  readOnly?: boolean;
  error?: ReactNode;
  description?: ReactNode;
  onBlur?: () => void;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  /** Başlangıç giriş biçimi (varsayılan LT). */
  defaultMode?: TimeEntryMode;
  /** Dış kabın sınıfı (ör. ızgarada tam satır). */
  className?: string | undefined;
  classNames?: {
    input?: string;
    label?: string;
    required?: string;
    description?: string;
    placeholder?: string;
  };
}

/**
 * LT + UTC tarih-saat girişi (ADR 0057 §2): kullanıcı alan başına yerel saatle (LT) ya da UTC ile
 * girer; girilen saatin yanında öteki saat salt okunur görünür. Saklanan değer her zaman UTC andır;
 * fark raporun saat farkıdır. Görünüm `GG.AA.YYYY SS:DD` (24 saat; `DateTimeInput`).
 */
export function DateTimeLtUtcInput({
  label,
  value,
  onChange,
  tzOffsetMin,
  required,
  readOnly = false,
  error,
  description,
  onBlur,
  leftSection,
  rightSection,
  defaultMode = 'lt',
  className,
  classNames,
}: DateTimeLtUtcInputProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<TimeEntryMode>(defaultMode);
  const twinId = useId();
  const offset = mode === 'lt' ? tzOffsetMin : 0;
  const zone = offsetLabel(tzOffsetMin);
  const twin = mode === 'lt' ? zonedStamp(value, 0) : zonedStamp(value, tzOffsetMin);
  const twinLabel = mode === 'lt' ? t('common.time.utc') : t('common.time.ltZone', { zone });
  return (
    <div className={[classes.wrap, className].filter(Boolean).join(' ')}>
      <div className={classes.root} data-mode={mode}>
        <DateTimeInput
          className={classes.main}
          label={`${label} (${mode === 'lt' ? t('common.time.lt') : t('common.time.utc')})`}
          withAsterisk={!!required && !readOnly}
          readOnly={readOnly}
          value={value ? zonedInput(value, offset) : ''}
          onChange={(local) => onChange(local ? fromZonedInput(local, offset) : null)}
          {...(onBlur ? { onBlur } : {})}
          {...(leftSection ? { leftSection } : {})}
          {...(rightSection !== undefined ? { rightSection } : {})}
          error={error ?? null}
          {...(description
            ? {
                description,
                inputWrapperOrder: ['label', 'input', 'description', 'error'] as const,
              }
            : {})}
          {...(classNames
            ? {
                classNames: {
                  input: classNames.input ?? '',
                  label: classNames.label ?? '',
                  required: classNames.required ?? '',
                  description: classNames.description ?? '',
                  placeholder: classNames.placeholder ?? '',
                },
              }
            : {})}
        />
        <div className={classes.twin}>
          <div className={classes.twinHead}>
            <span id={twinId} className={[classes.twinLabel, classNames?.label].join(' ')}>
              {twinLabel}
            </span>
            {readOnly ? null : (
              <SegmentedControl
                size="xs"
                className={classes.mode}
                aria-label={t('common.time.entryMode', { field: label })}
                value={mode}
                onChange={(next) => setMode(next as TimeEntryMode)}
                data={[
                  { value: 'lt', label: t('common.time.lt') },
                  { value: 'utc', label: t('common.time.utc') },
                ]}
              />
            )}
          </div>
          <output aria-labelledby={twinId} className={classes.twinValue}>
            {twin}
          </output>
        </div>
      </div>
    </div>
  );
}

export interface TzOffsetSelectProps {
  label: string;
  value: number;
  onChange: (offsetMin: number) => void;
  /** IANA adının farkı bu ana göre (raporun zamanı; yaz saati). */
  at: string | null;
  readOnly?: boolean;
  required?: boolean;
  classNames?: { input?: string; label?: string; required?: string };
}

/**
 * Saat farkı seçici (ADR 0057 §2): UTC±hh:mm listesi ve IANA bölge adıyla arama. Bölge seçilince
 * raporun anındaki farkı yazılır; bölge adı saklanmaz. Fark dakika hassasiyetindedir (ADR 0065 §8):
 * mevcut değer adıma uymasa da listededir, `+05:45` gibi yazılan fark da seçilebilir.
 */
export function TzOffsetSelect({
  label,
  value,
  onChange,
  at,
  readOnly = false,
  required,
  classNames,
}: TzOffsetSelectProps) {
  const { t } = useTranslation();
  const instant = at && !Number.isNaN(Date.parse(at)) ? Date.parse(at) : Date.now();
  // Bölge listesi raporun gününe göre bir kez (saat değişince yeniden değil)
  const day = Math.floor(instant / 86_400_000);
  const zones = useMemo(() => ianaZoneOptions(day * 86_400_000 + 43_200_000), [day]);
  const [search, setSearch] = useState('');
  const typed = readOnly ? null : parseOffset(search);
  const data = useMemo(
    () => [
      {
        group: t('common.time.offsetGroup'),
        items: offsetChoices(typed ?? value, offsetChoices(value)).map((m) => ({
          value: String(m),
          label: offsetLabel(m),
        })),
      },
      ...(readOnly
        ? []
        : [
            {
              group: t('common.time.zoneGroup'),
              items: zones.map((z) => ({
                value: `zone:${z.zone}`,
                label: `${z.zone.replace(/_/g, ' ')} (${offsetLabel(z.offset)})`,
              })),
            },
          ]),
    ],
    [zones, readOnly, t, value, typed],
  );
  return (
    <Select
      label={label}
      withAsterisk={!!required && !readOnly}
      searchable={!readOnly}
      onSearchChange={setSearch}
      nothingFoundMessage={t('common.noData')}
      data={data}
      value={String(value)}
      readOnly={readOnly}
      allowDeselect={false}
      {...(readOnly ? {} : { limit: 80 })}
      {...(readOnly ? { rightSection: null } : {})}
      onChange={(next) => {
        if (next === null) return;
        if (next.startsWith('zone:')) {
          const zone = zones.find((z) => `zone:${z.zone}` === next);
          if (zone) onChange(zone.offset);
          return;
        }
        onChange(Number(next));
      }}
      {...(classNames
        ? {
            classNames: {
              input: classNames.input ?? '',
              label: classNames.label ?? '',
              required: classNames.required ?? '',
            },
          }
        : {})}
    />
  );
}
